import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import PricingCard from '../components/PricingCard';
import { 
  Shield, Award, Clock, CheckCircle, Star, 
  TrendingUp, Users, Zap, Mail, Video 
} from 'lucide-react';
import { pricingPlans, testimonials, faqs } from '../mock/mockData';

export default function Home() {
  useEffect(() => {
    // Smooth scroll to section if hash present
    const hash = window.location.hash;
    if (hash) {
      setTimeout(() => {
        const element = document.querySelector(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#0078D4] via-[#005a9e] to-[#003366] text-white py-20 overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS1vcGFjaXR5PSIwLjA1IiBzdHJva2Utd2lkdGg9IjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-30"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
                <Award className="h-5 w-5 text-yellow-300" />
                <span className="text-sm font-semibold">20,000+ E5 Licenses Managed</span>
              </div>
              
              <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
                Expert Microsoft 365 Support On-Demand
              </h1>
              
              <p className="text-xl text-blue-100 mb-8">
                Get professional technical advice and assistance for Microsoft 365 and Azure AD from a seasoned expert. Your first query is completely free!
              </p>

              <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4 mb-8">
                <Link to="/register">
                  <Button size="lg" className="bg-white text-[#0078D4] hover:bg-gray-100 font-semibold px-8 h-14 text-lg">
                    Get Started Free
                  </Button>
                </Link>
                <a href="#how-it-works">
                  <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10 font-semibold px-8 h-14 text-lg">
                    Learn More
                  </Button>
                </a>
              </div>

              <div className="grid grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="text-3xl font-bold mb-1">24hr</div>
                  <div className="text-sm text-blue-100">Standard SLA</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold mb-1">1hr</div>
                  <div className="text-sm text-blue-100">Priority SLA</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold mb-1">FREE</div>
                  <div className="text-sm text-blue-100">First Query</div>
                </div>
              </div>
            </div>

            <div className="hidden lg:block">
              <div className="relative">
                <div className="absolute inset-0 bg-white/10 backdrop-blur-sm rounded-2xl transform rotate-3"></div>
                <Card className="relative bg-white p-8 shadow-2xl">
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="bg-[#0078D4] p-3 rounded-full">
                      <Shield className="h-8 w-8 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-[#003366]">Kaustubh Phatak</h3>
                      <p className="text-gray-600">Microsoft 365 Expert</p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="flex items-center space-x-3">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                      <span className="text-gray-700">20k E5 License Management</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                      <span className="text-gray-700">Microsoft Support Team Lead</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                      <span className="text-gray-700">Exchange, Teams, Azure AD Expert</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                      <span className="text-gray-700">PowerShell Automation Specialist</span>
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#003366] mb-4">
              Why Choose Solving365?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Leverage years of hands-on Microsoft 365 experience to solve your technical challenges quickly and efficiently
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card className="border-t-4 border-t-[#0078D4] hover:shadow-lg transition-shadow">
              <CardContent className="pt-8">
                <div className="bg-blue-50 p-3 rounded-full w-fit mb-4">
                  <Clock className="h-8 w-8 text-[#0078D4]" />
                </div>
                <h3 className="text-xl font-bold text-[#003366] mb-3">
                  Guaranteed Response Times
                </h3>
                <p className="text-gray-600">
                  24-hour SLA for standard queries, 1-hour for priority issues. We value your time and deliver on our promises.
                </p>
              </CardContent>
            </Card>

            <Card className="border-t-4 border-t-[#0078D4] hover:shadow-lg transition-shadow">
              <CardContent className="pt-8">
                <div className="bg-blue-50 p-3 rounded-full w-fit mb-4">
                  <Award className="h-8 w-8 text-[#0078D4]" />
                </div>
                <h3 className="text-xl font-bold text-[#003366] mb-3">
                  Proven Expertise
                </h3>
                <p className="text-gray-600">
                  Managed 20k+ E5 licenses, led Microsoft support teams, and resolved thousands of complex Microsoft 365 issues.
                </p>
              </CardContent>
            </Card>

            <Card className="border-t-4 border-t-[#0078D4] hover:shadow-lg transition-shadow">
              <CardContent className="pt-8">
                <div className="bg-blue-50 p-3 rounded-full w-fit mb-4">
                  <Zap className="h-8 w-8 text-[#0078D4]" />
                </div>
                <h3 className="text-xl font-bold text-[#003366] mb-3">
                  Comprehensive Solutions
                </h3>
                <p className="text-gray-600">
                  From troubleshooting to strategic advice to custom scripting - get complete solutions, not just quick fixes.
                </p>
              </CardContent>
            </Card>

            <Card className="border-t-4 border-t-[#0078D4] hover:shadow-lg transition-shadow">
              <CardContent className="pt-8">
                <div className="bg-blue-50 p-3 rounded-full w-fit mb-4">
                  <Video className="h-8 w-8 text-[#0078D4]" />
                </div>
                <h3 className="text-xl font-bold text-[#003366] mb-3">
                  Teams Meeting Support
                </h3>
                <p className="text-gray-600">
                  Complex issues? Schedule a Microsoft Teams meeting to walk through solutions together in real-time.
                </p>
              </CardContent>
            </Card>

            <Card className="border-t-4 border-t-[#0078D4] hover:shadow-lg transition-shadow">
              <CardContent className="pt-8">
                <div className="bg-blue-50 p-3 rounded-full w-fit mb-4">
                  <TrendingUp className="h-8 w-8 text-[#0078D4]" />
                </div>
                <h3 className="text-xl font-bold text-[#003366] mb-3">
                  Enterprise Experience
                </h3>
                <p className="text-gray-600">
                  Bring enterprise-grade expertise to your organization, regardless of size. Benefit from large-scale tenant management knowledge.
                </p>
              </CardContent>
            </Card>

            <Card className="border-t-4 border-t-[#0078D4] hover:shadow-lg transition-shadow">
              <CardContent className="pt-8">
                <div className="bg-blue-50 p-3 rounded-full w-fit mb-4">
                  <Mail className="h-8 w-8 text-[#0078D4]" />
                </div>
                <h3 className="text-xl font-bold text-[#003366] mb-3">
                  Detailed Documentation
                </h3>
                <p className="text-gray-600">
                  Receive comprehensive written solutions and documentation you can reference and share with your team.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#003366] mb-4">
              How It Works
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Getting expert Microsoft 365 support is simple and straightforward
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="bg-[#0078D4] text-white w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                1
              </div>
              <h3 className="text-xl font-bold text-[#003366] mb-3">Register</h3>
              <p className="text-gray-600">
                Create your free account using Microsoft SSO or email. Takes less than a minute.
              </p>
            </div>

            <div className="text-center">
              <div className="bg-[#0078D4] text-white w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                2
              </div>
              <h3 className="text-xl font-bold text-[#003366] mb-3">Submit Query</h3>
              <p className="text-gray-600">
                Describe your issue or requirement. Choose between Problem, Advise, or Scripting.
              </p>
            </div>

            <div className="text-center">
              <div className="bg-[#0078D4] text-white w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                3
              </div>
              <h3 className="text-xl font-bold text-[#003366] mb-3">Get Response</h3>
              <p className="text-gray-600">
                Receive expert guidance within SLA via email or Teams meeting.
              </p>
            </div>

            <div className="text-center">
              <div className="bg-[#0078D4] text-white w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                4
              </div>
              <h3 className="text-xl font-bold text-[#003366] mb-3">Resolve & Follow-up</h3>
              <p className="text-gray-600">
                Implement the solution with follow-up support included to ensure success.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#003366] mb-4">
              Transparent Pricing
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Choose the service level that matches your needs. Your first query is always free!
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {pricingPlans.map((plan) => (
              <PricingCard key={plan.id} plan={plan} />
            ))}
          </div>

          <div className="mt-12 text-center">
            <p className="text-gray-600 mb-4">
              Need multiple queries? Contact us for package pricing and volume discounts.
            </p>
            <a href="mailto:phatak.kau@live.com">
              <Button variant="outline" size="lg" className="border-[#0078D4] text-[#0078D4] hover:bg-blue-50">
                Contact for Custom Pricing
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#003366] mb-4">
              What Clients Say
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Don't just take our word for it - hear from satisfied clients
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="hover:shadow-lg transition-shadow">
                <CardContent className="pt-6">
                  <div className="flex items-center space-x-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-gray-700 mb-6 italic">
                    "{testimonial.content}"
                  </p>
                  <div className="flex items-center space-x-3">
                    <img 
                      src={testimonial.image} 
                      alt={testimonial.name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                    <div>
                      <p className="font-semibold text-[#003366]">{testimonial.name}</p>
                      <p className="text-sm text-gray-600">{testimonial.role}</p>
                      <p className="text-sm text-gray-500">{testimonial.company}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#003366] mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600">
              Everything you need to know about our service
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem 
                key={index} 
                value={`item-${index}`}
                className="bg-white border border-gray-200 rounded-lg px-6"
              >
                <AccordionTrigger className="text-left font-semibold text-[#003366] hover:text-[#0078D4]">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-700">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#0078D4] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">
            Ready to Solve Your Microsoft 365 Challenges?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Get started with your free first query today. No credit card required.
          </p>
          <Link to="/register">
            <Button size="lg" className="bg-white text-[#0078D4] hover:bg-gray-100 font-semibold px-12 h-14 text-lg">
              Start Your Free Query
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
